import { useMemo } from 'react';
import PropTypes from 'prop-types';

import {
  CloseBoldIcon,
  CompletedPromptIcon,
  EmptyPromptIcon,
  OverlayTriggerTooltip,
} from '@utilities';

import styles from './_index.module.scss';

const TYPES_WITH_NEW_OPTIONAL_PROMPTS = ['Narrative'];

const Prompts = ({
  activePrompt,
  getPillTooltip,
  getPillValue,
  prompts,
  setActivePrompt,
  setSelectedAnswers,
  transformedAnswersMap,
  type,
}) => {
  const getPromptAnswers = (prompt) =>
    prompt?.groupedPrompts
      ?.map((groupedPrompt) => transformedAnswersMap?.[groupedPrompt.promptKey] || [])
      .flat() ||
    transformedAnswersMap[prompt.promptKey] ||
    [];

  const onClearAnswer = (prompt) => {
    const promptKeys = prompt?.groupedPrompts?.length
      ? prompt.groupedPrompts.map((groupedPrompt) => groupedPrompt.promptKey)
      : [prompt.promptKey];

    setSelectedAnswers((prev) =>
      promptKeys.reduce(
        (acc, promptKey) => ({
          ...acc,
          [promptKey]: [],
        }),
        { ...prev }
      )
    );

    setActivePrompt((current) => {
      if (current?.promptKey !== prompt.promptKey) {
        return prompt;
      }
      return current;
    });
  };
  const hasNewOptionalPrompts = TYPES_WITH_NEW_OPTIONAL_PROMPTS.includes(type);

  const sortedPrompts = useMemo(() => {
    if (!prompts?.length) return { optional: [], required: [] };

    const required = [];
    const optional = [];

    prompts.forEach((prompt) => {
      const isRequired = prompt?.isRequired || prompt.isRequiredForModal;

      if (isRequired) {
        required.push(prompt);
      } else if (prompt?.groupedPrompts?.length > 0) {
        optional.push(prompt);
      } else {
        optional.push(prompt);
      }
    });

    return { optional, required };
  }, [prompts]);

  const renderPrompt = (prompt) => {
    const isActive = activePrompt?.promptKey === prompt.promptKey;
    const currentPromptAnswers = getPromptAnswers(prompt);
    const value = getPillValue({
      firstAnswerMaxLength: 13,
      prompt,
      promptAnswers: currentPromptAnswers,
    });

    const tooltip = getPillTooltip({
      prompt,
      promptAnswers: currentPromptAnswers,
    });

    return (
      <div
        className={isActive ? styles['is-active'] : ''}
        key={prompt.promptKey}
        onClick={() => setActivePrompt(prompt)}
      >
        <div className={styles['prompt-icon']} key={`${prompt.promptKey}-icon`}>
          {currentPromptAnswers.length > 0 ? (
            <CompletedPromptIcon />
          ) : (
            <EmptyPromptIcon
              className={`${styles['empty-icon']} ${isActive ? styles['is-active'] : ''}`}
            />
          )}
        </div>

        <div key={`${prompt.promptKey}-content`}>
          <p className={`${styles['prompt-name']} ${isActive ? styles['prompt-name-active'] : ''}`}>
            {prompt.name}
            {!hasNewOptionalPrompts && prompt.isRequiredForModal && (
              <span className={styles['prompt-required']}>*</span>
            )}
          </p>
          {currentPromptAnswers?.length > 0 && (
            <OverlayTriggerTooltip content={tooltip} isFullWidth>
              <div className={styles['prompt-value-container']}>
                <p className={styles['prompt-value']}>{value}</p>
                <CloseBoldIcon onClick={() => onClearAnswer(prompt)} />
              </div>
            </OverlayTriggerTooltip>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={styles['prompts']}>
      {hasNewOptionalPrompts ? (
        <>
          {sortedPrompts.required?.map(renderPrompt)}
          {sortedPrompts.optional?.length > 0 && (
            <p className={styles['prompt-optional-header']}>OPTIONAL</p>
          )}
          {sortedPrompts.optional?.map(renderPrompt)}
        </>
      ) : (
        prompts?.map(renderPrompt)
      )}
    </div>
  );
};

Prompts.propTypes = {
  activePrompt: PropTypes.object.isRequired,
  getPillTooltip: PropTypes.func,
  getPillValue: PropTypes.func,
  prompts: PropTypes.array.isRequired,
  setActivePrompt: PropTypes.func.isRequired,
  setSelectedAnswers: PropTypes.func.isRequired,
  transformedAnswersMap: PropTypes.object.isRequired,
  type: PropTypes.string,
};

export default Prompts;
